/*
 * ew-tune-builder.js — Tune Builder, V1 (Phase B)
 * (Formerly ew-path-finder.js / "Tune Path Finder"; renamed 2026-05-29.)
 *
 * The tunes-first engine at /pages/configure. Visitor declares what's installed →
 * two priced choices. Table-driven per STAGE-DEFINITIONS.md — never
 * "configurator" publicly. No hp/tq in V1 (V2); a power slot is left.
 *
 * Stage thresholds (STAGE-DEFINITIONS.md):
 *   0 bone stock · 1 a tune on stock OR any 1+ core part (S2 incomplete)
 *   2 S2 Core complete · 3 S2+S3 Core complete · 4+ built/standalone → phone.
 * A lone advanced part does NOT promote (orphaned — see logic).
 * Path 1 = current-stage tune. Path 2 "Level Up" = the complete gap kit
 * for the next stage (or an orphaned part's native stage) minus owned
 * parts + the tune, 15% off à-la-carte, "You save $X".
 *
 * Engine management: piggyback ⇒ conversion play (recommend the bm3
 * flash for the real hardware stage + attach the ENET cable);
 * standalone / built internals ⇒ Stage 4+ phone-consult dialog.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-pf]');
  if (!root) return;
  var EW_LOGO_URL = (root && root.getAttribute('data-ew-logo-url')) || '';
  var D = window.ewPathFinderData || { tunes: [], hardware: [], cable: null };
  /* Per the MODEL-RESET (claude-code-prompts/_status/MODEL-RESET.md):
       Kit (standing pre-defined product) = Σ × 0.92 (8% off)
       Bundle (dynamic configurator output) = Σ × 0.95 (5% off)
     The Path Finder's per-part path = configurator output → BUNDLE_DISCOUNT.
     Standing kits are picked up via their own pre-set variant.price
     (which equals Σ × 0.92) so KIT_DISCOUNT is informational only.
     Neither percentage is rendered in UI — the price tells the story.

     Phase 2 2E: source the constants from the shared gap engine module
     (assets/ew-gap-engine.js) so both surfaces (homepage Path Finder +
     kit PDP configurator) share the math. Local fallback values preserve
     behaviour if the gap engine hasn't loaded yet — the layout/theme.liquid
     <script defer> order ensures ew-gap-engine.js loads BEFORE
     ew-tune-builder.js, but the fallback keeps the page from breaking on
     edge-cache race conditions. */
  var GAP   = (typeof window !== 'undefined' && window.ewGapEngine) || null;
  var BUNDLE_DISCOUNT = GAP ? Math.round(GAP.BUNDLE_DISCOUNT * 100) : 5;  /* % off, integer */
  var KIT_DISCOUNT    = GAP ? Math.round(GAP.KIT_DISCOUNT    * 100) : 8;

  /* ---- per-engine Core matrix (STAGE-DEFINITIONS.md, encoded) ----
     V8 note (2026-05 spec revision):
       - S63 Stage 2 = downpipes + CSF chargecooler radiator.
         No aftermarket V8 charge pipe is sourced/pursued — removed.
       - N63 Stage 2 = catless downpipes only.
         No N63 charge pipe or cooling part is sourceable — both removed
         (CSF doesn't fit N63; Airtec/Mishimoto don't make one).
       See PARTS-SOURCING-MATRIX.md section 2 + STAGE-DEFINITIONS.md. */
  var STAGE_TABLES = {
    N13: { 2: ['downpipe', 'charge_pipe', 'intercooler'], 3: ['fuel_pump'] },
    B48: { 2: ['downpipe', 'charge_pipe', 'intercooler'], 3: ['turbo', 'fuel_pump'] },
    B58: { 2: ['downpipe', 'charge_pipe', 'intercooler'], 3: ['fuel_pump'] },
    N55: { 2: ['downpipe', 'charge_pipe', 'intercooler'], 3: ['turbo'] },
    S55: { 2: ['downpipe', 'charge_pipe', 'chargecooler'], 3: ['port_injection', 'fuel_pump'] },
    S58: { 2: ['downpipe', 'charge_pipe', 'chargecooler'], 3: ['port_injection', 'fuel_pump'] },
    N20: { 2: ['downpipe', 'charge_pipe', 'intercooler'], 3: ['fuel_pump'] },
    S63: { 2: ['downpipe', 'chargecooler'],                3: ['fuel_pump', 'turbo'] },
    N63: { 2: ['downpipe'],                                3: ['fuel_pump', 'turbo'] }
  };
  /* native stage of a core part — for orphaned-part detection */
  var PART_STAGE = {
    downpipe: 2, charge_pipe: 2, intercooler: 2, chargecooler: 2,
    turbo: 3, lpfp: 3, hpfp: 3, port_injection: 3, water_meth: 3
  };
  var CAT_LABEL = {
    downpipe: 'downpipe', charge_pipe: 'charge pipe(s)', intercooler: 'intercooler',
    chargecooler: 'chargecooler radiator', turbo: 'upgraded turbo', fuel_pump: 'upgraded fuel pump',
    lpfp: 'LPFP', hpfp: 'HPFP', port_injection: 'port injection', water_meth: 'water/meth injection'
  };

  /* §4 livery voice — Path 2 CTA (placeholder copy) */
  var VOICE = {
    neutral:     'Level up',
    performance: 'Build your power path',
    drifter:     'Set it up to slide',
    oemplus:     'Upgrade without the risk'
  };
  function voiceCta() { return VOICE[document.body.getAttribute('data-livery')] || VOICE.neutral; }

  var state = { vehicle: null, em: 'stock', mods: {}, fuel: '93' };

  /* ---- funnel-event tracking (G-1 Commit C) ----
     ewTrack is the global analytics helper (owned by ew-global.js). Call it
     defensively — it may not have loaded. These flags fire-once / de-noise
     the builder funnel events:
       _trackStarted  — tune_builder_start fires once on first real compute()
       _lastStage     — last stage value emitted to tune_builder_stage_detected
                        (null = none yet) so we only fire on a genuine change. */
  var _trackStarted = false;
  var _lastStage = null;

  /* ---- helpers ---- */
  /* Presentment-aware (INTL-2): ewMoney (ew-global.js, loaded first) formats
     cents against the active Market's money format. These cents come from
     {{ variant.price }} — already presentment cents under Markets. The
     `$`+toLocaleString fallback preserves the whole-dollar USD render if
     ew-global.js hasn't defined ewMoney (edge-cache race). */
  function money(c) {
    if (typeof window.ewMoney === 'function') return window.ewMoney(c, { decimals: false });
    var n = Number(c); if (!isFinite(n)) n = 0;
    return '$' + (n / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  /* Tuning-stage badge — emits the SAME DOM as snippets/stage-badge.liquid so
     the shared .ew-stage-badge CSS (assets/ew-components.css) is the single
     styling source. The Builder result surface is dark, so we render the dark
     variant (no --light). Text "STAGE n" is always a literal string. Non-
     interactive label, never a CTA. stage is the numeric stage (1|2|3); the
     Builder never resolves a 2+ here. Unknown stage → empty string. */
  function stageBadge(stage) {
    var n = parseInt(stage, 10);
    if (n !== 1 && n !== 2 && n !== 3) return '';
    return '<span class="ew-stage-badge ew-stage-badge--s' + n +
      ' ew-stage-badge--sm" data-stage-level="' + n + '">' +
      '<span class="ew-stage-badge__rail" aria-hidden="true"></span>STAGE ' + n + '</span>';
  }
  function readVehicle() {
    try { return JSON.parse(localStorage.getItem('ew_active_vehicle') || 'null'); } catch (_) { return null; }
  }

  /* ---- URL-param vehicle (Item 6) ----
     A chassis-hub "Build this stage" CTA or any deep link can arrive as
     /pages/configure?chassis=g20 (optionally &engine=b58, or &vehicle=).
     The param WINS over the stored Garage vehicle for this entry, then
     persists to the Garage so it carries on. We resolve the engine from
     the shared chassis dataset (the static JSON asset loaded by
     window.EWVehicleResolver) so the matcher always has an engine
     family — a chassis-only vehicle would otherwise fall through
     STAGE_TABLES[''] straight to the "not mapped" fallback. */
  function chassisDataset() {
    /* BUGS-1 Bug 3 — single dataset/parse path. The chassis dataset is the
       static JSON asset, loaded ONCE by the shared resolver
       (window.EWVehicleResolver, ew-vehicle-resolver.js). We no longer parse
       the retired metaobject island content; we read the resolver's loaded
       array. It may be empty until EWVehicleResolver.ready() has resolved —
       init() awaits ready() before calling vehicleFromParams() for deep links. */
    return (window.EWVehicleResolver && window.EWVehicleResolver.data && window.EWVehicleResolver.data()) || [];
  }
  function vehicleFromParams() {
    var qs;
    try { qs = new URLSearchParams(window.location.search); } catch (_) { return null; }
    var chassis = String(qs.get('chassis') || qs.get('vehicle') || '').toUpperCase().trim();
    if (!chassis) return null;
    var engine = String(qs.get('engine') || '').toUpperCase().trim();
    var trimParam = String(qs.get('trim') || '').trim();
    var rows = chassisDataset().filter(function (c) {
      return String(c.code || '').toUpperCase().trim() === chassis;
    });
    /* WO-C3 — the chassis IS in the dataset but no row carries this engine, so
       the param is wrong. Fall through to Step 1 instead of silently keeping
       every chassis row and resolving to rows[0], which is a different car
       (?chassis=F30&engine=ZZZ used to land on the 316i). The rows-empty case
       is deliberately excluded: that chassis is absent from the dataset and the
       tuneFor() fallback below still owns it. */
    if (engine && rows.length) {
      var byEng = rows.filter(function (c) { return engineFamily(c.engine) === engineFamily(engine); });
      if (!byEng.length) return null;
      rows = byEng;
    }
    /* Honor an explicit trim param (the Garage dialog's tune handoff carries the
       picked trim) so a shared deep-link resolves the same record, not just the
       first row for the chassis. */
    if (trimParam) {
      var byTrim = rows.filter(function (c) { return String(c.trim || '').toLowerCase() === trimParam.toLowerCase(); });
      if (byTrim.length) rows = byTrim;
    }
    var row = rows[0];
    if (!row) {
      /* No chassis-dataset record — derive the engine from a matching
         tune so the matcher still resolves instead of falling back. */
      var t = tuneFor(chassis, '');
      if (!t) return null;
      return { code: chassis, engine_code: engine || ((t.engine && t.engine[0]) || ''),
               trim: trimParam || '', label: chassis, source: 'deep-link' };
    }
    /* F-05 — a deep link knows the chassis row, NOT the visitor's model
       year: carry the honest ys/ye band, never fabricate a year from the
       band end. Label via the canonical shared formatter when the resolver
       is up (the deep-link path awaits ready() before calling here); the
       inline fallback renders the same band form, year-free. */
    var v = {
      code: chassis,
      /* WO-C3 — the matched row wins over the raw param. The row is the
         authoritative record, so a link saying &engine=S58 still stores the
         full S58B30T0, and a junk param can never reach the Garage. */
      engine_code: row.engine || engine || '',
      make: row.make || '',
      model: row.model || '',
      trim: row.trim || trimParam || '',
      ys: row.ys || '',
      ye: row.ye || '',
      source: 'deep-link'
    };
    var R = window.EWVehicleResolver;
    v.label = (R && typeof R.displayLabel === 'function')
      ? R.displayLabel(v)
      : ((row.ys && row.ye)
          ? [row.ys + '–' + row.ye, chassis, row.model].filter(Boolean).join(' ')
          : ([row.make, row.model].filter(Boolean).join(' ') || chassis));
    return v;
  }
  function persistVehicle(v) {
    if (window.EWVehicleResolver && typeof window.EWVehicleResolver.write === 'function') {
      window.EWVehicleResolver.write(v); /* persists + dispatches ew:vehicle-resolved */
    } else {
      try { localStorage.setItem('ew_active_vehicle', JSON.stringify(v)); } catch (_) {}
    }
  }
  function stripVehicleParams() {
    try {
      var u = new URL(window.location.href);
      ['chassis', 'vehicle', 'engine', 'trim'].forEach(function (k) { u.searchParams.delete(k); });
      window.history.replaceState({}, '', u.pathname + (u.search || '') + u.hash);
    } catch (_) {}
  }
  function chassisOf(v) { return v ? String(v.code || v.chassis_code || v.chassis || '').toUpperCase().trim() : ''; }
  /* R2 2.1 — one implementation, in assets/ew-engine-family.js. Chassis
     metaobjects + tune metafields use mixed formats — short "B48" and full
     "B48B20O1" — so we match on family, never on an exact string and never
     on trim. */
  var engineFamily = window.EWEngineFamily;
  function engineOf(v) { return v ? engineFamily(v.engine_code || v.engine || '') : ''; }
  function vehicleLabel(v) {
    if (!v) return '';
    /* F-05 — delegate to THE canonical formatter (year only with
       provenance; honest ys–ye band otherwise). The inline fallback for
       the resolver-absent edge builds from fields and gates the year on
       year_known — never v.label first, which on a legacy value carries
       the fabricated band-end year. */
    var R = window.EWVehicleResolver;
    if (R && typeof R.displayLabel === 'function') {
      var s = R.displayLabel(v);
      if (s) return s;
    }
    var yr = (v.year_known === true) ? v.year : null;
    return [yr, v.make, v.model, v.code || v.chassis_code || v.chassis]
      .filter(Boolean).join(' ') || v.label || '';
  }
  /* is a required core category satisfied by the declared build */
  function meets(cat, m) {
    if (cat === 'fuel_pump') return !!(m.lpfp || m.hpfp);
    return !!m[cat];
  }
  function coreSet(engine, stage) {
    var t = STAGE_TABLES[engine]; var r = [];
    if (!t) return r;
    if (stage >= 2 && t[2]) r = r.concat(t[2]);
    if (stage >= 3 && t[3]) r = r.concat(t[3]);
    return r;
  }
  /* hardware-only current stage (engine management piggyback/standalone
     are handled separately; custom_tune counts as "a tune on stock").
     Stage 3 = ALL of Stage 2 Core + ANY-OF Stage 3 Core, per
     STAGE-DEFINITIONS.md §74–87 (the Stage 3 row reads as fueling or
     turbo, with the doc winning on conflict). Stage 2 = ALL of Stage 2
     Core, unchanged. */
  function currentStage(engine, m, em) {
    if (m.built_internals || em === 'standalone') return 4;
    var t = STAGE_TABLES[engine];
    if (!t) return 0;
    var s2 = t[2] || [], s3 = t[3] || [];
    var s2Met = s2.every(function (c) { return meets(c, m); });
    if (s2Met && s3.some(function (c) { return meets(c, m); })) return 3;
    if (s2Met) return 2;
    var anyPart = Object.keys(PART_STAGE).some(function (c) { return meets(c, m); });
    if (anyPart || em === 'custom_tune') return 1;
    return 0;
  }
  /* owned core parts whose native stage is above the current stage */
  function orphanStage(m, stage) {
    var top = 0;
    Object.keys(PART_STAGE).forEach(function (c) {
      if (meets(c, m) && PART_STAGE[c] > stage && PART_STAGE[c] > top) top = PART_STAGE[c];
    });
    return top; /* 0 = none */
  }
  function tuneFor(chassis, engine) {
    var byBoth = null, byChassis = null;
    for (var i = 0; i < D.tunes.length; i++) {
      var t = D.tunes[i];
      if (!(t.chassis || []).some(function (c) { return String(c).toUpperCase().trim() === chassis; })) continue;
      if (!byChassis) byChassis = t;
      if ((t.engine || []).some(function (e) { return engineFamily(e) === engine; })) {
        byBoth = byBoth || t;
      }
    }
    return byBoth || (engine ? null : byChassis);
  }
  /* cheapest catalog hardware filling a gap category, for this car.
     Fitment rule (2026-05-29) — the safe reading of "chassis OR engine":
     chassis is authoritative WHEN the part lists chassis fitment, so we
     require it to match (and the engine too when both are known) — we
     never surface an F56-only intercooler for a G20 just because they
     share the B48 engine. A part that carries NO chassis list
     (engine-universal) falls back to an engine match. This surfaces every
     genuinely-fitting part (incl. the Phantom turbo, whose comprehensive
     ew_ chassis list includes the resolved chassis) without false fitment. */
  function hwFor(chassis, engine, cat) {
    var wants = cat === 'fuel_pump' ? ['hpfp', 'lpfp'] : [cat];
    /* Marque guard (B48-SPLIT) — the i4 turbo lives as one product with
       per-marque variants ("BMW"/"MINI") that share the B48 engine family.
       Engine-only matching (incl. the no-chassis fallback below) would let a
       MINI car pick the BMW turbo variant or vice-versa. When BOTH the row
       and the vehicle carry a marque, they must agree; the guard is inert if
       either side is absent, so I6/V8 turbos and all other hardware (no
       row-level marque) are completely unaffected. */
    var vMarque = String((state.vehicle && state.vehicle.make) || '').toUpperCase().trim();
    var pool = D.hardware.filter(function (h) {
      if (wants.indexOf(h.category) === -1) return false;
      var hMarque = String(h.marque || '').toUpperCase().trim();
      if (hMarque && vMarque && hMarque !== vMarque) return false;
      var hasChassis = h.chassis && h.chassis.length;
      var hasEngine  = h.engine && h.engine.length;
      var chassisOk = hasChassis && h.chassis.some(function (c) { return String(c).toUpperCase().trim() === chassis; });
      var engineOk  = hasEngine && engine && h.engine.some(function (e) { return engineFamily(e) === engine; });
      if (hasChassis) {
        if (!chassisOk) return false;
        return (hasEngine && engine) ? engineOk : true;
      }
      return engineOk; /* engine-universal part (no chassis list) */
    });
    pool.sort(function (a, b) { return a.price - b.price; });
    return pool[0] || null;
  }
  /* F-01c — availability honesty. The data island now serializes
     variant.available (the canonical Liquid signal) on every tune stage,
     hardware row, bundle, and the cable. A MISSING key (a stale cached
     island) reads as available, so the guard fails OPEN to today's
     behavior — it only ever bites on an explicit available:false. */
  function isSellable(entry) { return !entry || entry.available !== false; }

  /* CAPTURE-SHARED (CS-3b) — product handle from a /products/<handle> url, for
     the Path-2 capture build (ewConfigurator's model carries product_handle).
     Tolerates absent/odd urls (returns '') and strips any ?query / #hash. */
  function handleFromUrl(u) {
    if (!u) return '';
    var m = String(u).split('/products/')[1];
    return m ? m.split(/[?#]/)[0] : '';
  }

  /* ---- step machine ---- */
  function showStep(name) {
    root.querySelectorAll('[data-pf-step]').forEach(function (el) {
      el.hidden = el.getAttribute('data-pf-step') !== name;
    });
    /* Any input step (vehicle/mods) reverts the header; the result state
       sets its own stage header in compute() before this runs. This also
       covers "start over" + "change vehicle" returning to the input copy. */
    if (name !== 'result') setHead('input');
    if (name !== 'vehicle') {
      window.scrollTo({ top: root.getBoundingClientRect().top + window.pageYOffset - 90, behavior: 'auto' });
    }
  }
  function paintVehicle() {
    root.querySelectorAll('[data-pf-vehicle-label]').forEach(function (el) {
      el.textContent = state.vehicle ? vehicleLabel(state.vehicle) : 'No vehicle selected';
    });
  }

  /* F-05 cold-load repaint guard. The stored-vehicle init paths paint ONCE
     synchronously, but window.EWVehicleResolver (deferred island) may not be
     defined yet — so vehicleLabel()'s displayLabel delegation is skipped and
     the honest field-fallback paints (correct year, missing engine-family
     parenthetical). When that's the case, schedule a SINGLE repaint once the
     resolver appears: paintVehicle() re-renders the label from state.vehicle
     through the now-available canonical formatter — no format string is
     duplicated here, displayLabel stays the sole source of the F-05 format.
     A year-known vehicle's displayLabel needs no dataset, so gate only on
     displayLabel existing (not ready()). Bounded poll, then give up silently
     (the field-fallback already shows the correct year). */
  function repaintWhenResolverReady() {
    var R = window.EWVehicleResolver;
    if (R && typeof R.displayLabel === 'function') return; /* already correct */
    var tries = 0;
    (function awaitResolver() {
      var RR = window.EWVehicleResolver;
      if (RR && typeof RR.displayLabel === 'function') { paintVehicle(); return; }
      if (++tries > 50) return; /* give up — honest field-fallback stands */
      setTimeout(awaitResolver, 100);
    })();
  }

  /* ---- state-aware page header ----
     Input state shows the section-setting copy (captured once as the
     original). The result state declares the detected stage, bound to the
     SAME p1Stage the matcher already computed for Path A — never recomputed,
     so the eyebrow, the headline, and Path A can't disagree. Text is swapped
     in place on the existing elements (no add/remove → no layout flash). */
  var headEls = null, headOrig = null;
  function headRefs() {
    if (headEls) return headEls;
    var head = root.querySelector('.ew-pf__head');
    if (!head) return null;
    headEls = {
      eyebrow: head.querySelector('[data-pf-head-eyebrow]'),
      title:   head.querySelector('[data-pf-head-title]'),
      intro:   head.querySelector('[data-pf-head-intro]')
    };
    headOrig = {
      eyebrow: headEls.eyebrow ? headEls.eyebrow.textContent : '',
      title:   headEls.title ? headEls.title.textContent : '',
      intro:   headEls.intro ? headEls.intro.textContent : ''
    };
    return headEls;
  }
  function setHeadText(eyebrow, title, intro) {
    var h = headRefs(); if (!h) return;
    if (h.eyebrow) h.eyebrow.textContent = eyebrow;
    if (h.title)   h.title.textContent   = title;
    if (h.intro)   h.intro.textContent   = intro;
  }
  /* mode: 'input' | 'stage' (needs n) | 's4' | 'unmapped' */
  function setHead(mode, n) {
    if (!headRefs()) return;
    if (mode === 'stage') {
      setHeadText('YOUR BUILD — STAGE ' + n, 'You’re running Stage ' + n + '.',
        'Two priced paths — run it now, or go further with the kit.');
    } else if (mode === 's4') {
      setHeadText('YOUR BUILD — STAGE 4+', 'This one needs a conversation.',
        'Built engines and standalone setups live off the menu — let’s talk it through.');
    } else if (mode === 'unmapped') {
      setHeadText('YOUR BUILD', 'Let’s map your build.',
        'We tune more than the catalog lists — tell us what you’re running and we’ll map it.');
    } else { /* input — revert to the server-rendered copy */
      setHeadText(headOrig.eyebrow, headOrig.title, headOrig.intro);
    }
  }

  /* ---- compute ---- */
  function compute() {
    /* CAPTURE-SHARED (CS-3b) — hide the capture beat at the START of every
       compute, before any branch logic. The priced Level-Up kit branch is the
       ONLY path that re-reveals it (below). This guarantees the early-return
       branches (unmapped, Stage 4+) and the non-priced branches (talk panel /
       no-parts) never leave a stale capture card from a prior priced compute
       lingering. p2Build stays null on every non-priced-kit branch. */
    var p2Build = null;
    var capWrap = root.querySelector('[data-ew-build-capture-wrap]');
    if (capWrap) capWrap.hidden = true;

    /* tune_builder_start — first genuine engagement (first compute, i.e. the
       user clicked through to a result), NOT mere page load. The _trackStarted
       flag guards against double-fire across recomputes / re-entries. Vehicle
       context may be empty here if the user hasn't picked yet — that's fine. */
    if (!_trackStarted) {
      _trackStarted = true;
      try {
        window.ewTrack && window.ewTrack('tune_builder_start', {
          chassis_code: chassisOf(state.vehicle),
          engine_code:  engineOf(state.vehicle)
        });
      } catch (e) {}
    }
    state.mods = {};
    root.querySelectorAll('[data-pf-mod]:checked').forEach(function (cb) {
      state.mods[cb.getAttribute('data-pf-mod')] = true;
    });
    var emEl = root.querySelector('[data-pf-em]:checked');
    state.em = emEl ? emEl.value : 'stock';
    var fuelEl = root.querySelector('[data-pf-fuel]:checked');
    state.fuel = fuelEl ? fuelEl.value : '93';

    var chassis = chassisOf(state.vehicle), engine = engineOf(state.vehicle);
    var out = root.querySelector('[data-pf-results]');
    var tune = tuneFor(chassis, engine);

    if (!tune || !STAGE_TABLES[engine]) {
      out.innerHTML = talkPanel("We don't have your exact " + (chassis || 'vehicle') +
        (engine ? ' (' + engine + ')' : '') + ' calibration mapped yet.',
        'We tune more than the catalog lists — tell us the build.');
      setHead('unmapped'); showStep('result'); return;
    }

    var stage = currentStage(engine, state.mods, state.em);

    /* tune_builder_stage_detected — fire only when the detected stage actually
       CHANGES (recompute on every mod toggle would otherwise spam it). _lastStage
       tracks the last value emitted; reset is unnecessary — a new vehicle that
       resolves the same stage is genuinely the same detection. */
    if (stage !== _lastStage) {
      _lastStage = stage;
      try {
        window.ewTrack && window.ewTrack('tune_builder_stage_detected', {
          detected_stage: stage,
          chassis_code:   chassis,
          engine_code:    engine
        });
      } catch (e) {}
    }

    /* Standalone or built internals → Stage 4+, the whole result is the dialog trigger. */
    if (stage >= 4) { out.innerHTML = s4Panel(true); setHead('s4'); showStep('result'); wireS4(); return; }

    /* ---- Path 1 ---- */
    var p1Stage = stage === 0 ? 1 : stage;
    var s1 = tune.stages[String(p1Stage)];
    /* F-01c §4 — defense in depth: tunes are untracked/CONTINUE so this
       should almost never trip, but an unavailable tune variant must never
       render a live buy CTA. Unavailable → disabled CTA + live consult. */
    var p1Sellable = isSellable(s1);
    var p1Items = s1 ? [{ id: s1.variantId, label: tune.title + ' · Stage ' + p1Stage, price: s1.price }] : [];
    var p1Note, p1Tag = 'Right for your setup now';
    if (state.em === 'piggyback') {
      p1Tag = 'Convert to a real flash';
      p1Note = 'You\'re on a piggyback — it fools sensors instead of rewriting the tables, leaving power and safety margin on the table. This is the bootmod3 flash for the hardware you actually have: real table tuning of your Stage ' + p1Stage + ' build.';
      if (D.cable && s1) p1Items.push({ id: D.cable.variantId, label: D.cable.title, price: D.cable.price });
    } else {
      p1Note = 'Custom bootmod3 Stage ' + p1Stage + ' calibration matched to your declared build.';
    }
    var html = '<article class="ew-pf-choice ew-pf-choice--c1">' +
      '<div class="ew-pf-choice__tag">' + p1Tag + '</div>' +
      '<h3 class="ew-pf-choice__title">' + tune.title + ' — Stage ' + p1Stage + '</h3>' +
      '<ul class="ew-pf-choice__lines"><li>' + p1Note + '</li>' +
        '<li>Calibrated for ' + state.fuel + (state.fuel.indexOf('E') === 0 ? '' : ' octane') + '.</li>' +
        (state.em === 'piggyback' && D.cable ? '<li>Includes the ' + D.cable.title + ' — you need it to run our flash.</li>' : '') +
      '</ul>' +
      '<div class="ew-pf-choice__power" data-pf-power aria-hidden="true"></div>' +
      /* F-01b — pre-rendered (empty) aria-live region: present in the DOM
         from first paint so a later failure announcement is reliably picked
         up by AT. showAddFailure() fills it; clearAddError() empties it. */
      '<p class="ew-pf-choice__error" data-pf-error role="status" aria-live="polite"></p>' +
      '<div class="ew-pf-choice__buy">' +
        '<span class="ew-pf-choice__price">' +
          (p1Items.length ? money(p1Items.reduce(function (a, it) { return a + it.price; }, 0)) : '—') + '</span>' +
        (p1Items.length
          ? (p1Sellable
              ? '<button type="button" class="ew-pf-choice__cta" data-pf-add="' +
                  encodeURIComponent(JSON.stringify(p1Items)) + '">Add to cart</button>'
              : '<button type="button" class="ew-pf-choice__cta" disabled>Add to cart</button>' +
                '<button type="button" class="ew-pf-choice__cta" data-pf-open-s4>Talk to us &rarr;</button>')
          : '') +
      '</div></article>';

    /* ---- Path 2 — Level Up ---- */
    var orphan = orphanStage(state.mods, stage);
    var target = orphan || (stage === 0 ? 2 : stage + 1);
    if (target >= 4 || stage >= 3) {
      html += s4Panel(false);
    } else {
      var nextTune = tune.stages[String(target)];
      var gap = coreSet(engine, target).filter(function (c) { return !meets(c, state.mods); });
      var kit = [], missing = [];
      gap.forEach(function (c) {
        var hw = hwFor(chassis, engine, c);
        if (hw) kit.push({ cat: c, hw: hw }); else missing.push(c);
      });
      html += renderLevelUp(target, nextTune, kit, missing, orphan > 0);

      /* CAPTURE-SHARED (CS-3b) — assemble the Path-2 capture build ONLY on the
         priced Level-Up kit branch (a real next-stage tune AND ≥1 priced part).
         This mirrors renderLevelUp's priced path; the no-tune / no-recipe
         (talkPanelLevel) and no-parts (renderNoParts) sub-branches leave
         p2Build null, so the capture beat stays hidden there.
         Shape matches ewConfigurator's model EXACTLY — note price (cents in the
         Tune Builder) → price_cents (what toSummary/toShareUrl read). */
      if (nextTune && nextTune.variantId && kit.length) {
        var _v = state.vehicle || {};
        p2Build = {
          vehicle: {
            code: chassis,
            make: _v.make || null,
            model: _v.model || null,
            trim: _v.trim || null,
            engine_code: _v.engine_code || null,
            year: (_v.year_known === true && _v.year) ? _v.year : null
          },
          stage: {
            stage_level: 's' + target,
            variant_id: nextTune.variantId,
            price_cents: nextTune.price,
            product_handle: handleFromUrl(tune.url)
          },
          options: kit.map(function (k) {
            return {
              variant_id: k.hw.variantId,
              price_cents: k.hw.price,
              quantity: 1,
              product_handle: handleFromUrl(k.hw.url)
            };
          })
        };
      }
    }

    out.innerHTML = html;
    /* Declare the detected stage in the header — bound to p1Stage, the SAME
       value Path A's title was built from above (never recomputed). */
    setHead('stage', p1Stage);
    showStep('result');
    wireS4();

    /* CAPTURE-SHARED (CS-3b) — reveal + mount the shared capture beat on the
       priced Level-Up kit branch; otherwise keep it hidden (it was already
       re-hidden at the top of compute()). Re-mount on every recompute refreshes
       the hosted build + the share attrs (mount() is idempotent on listeners). */
    var capEl = capWrap && capWrap.querySelector('[data-ew-build-capture]');
    if (p2Build && capEl && window.EWBuildCapture) {
      capWrap.hidden = false;
      window.EWBuildCapture.mount(capEl, p2Build);
    } else if (capWrap) {
      capWrap.hidden = true;
    }
  }
  function renderLevelUp(target, nextTune, kit, missing, isOrphan) {
    /* Only fall back to the generic "talk to us" panel when the tune is
       missing OR the WHOLE recipe is missing (no kit AND no TBA tiles).
       With TBA tiles, a partly-sourced recipe (V8 downpipe deferred but
       chargecooler in catalog) still renders the priced kit + an inline
       TBA tile for the deferred part. */
    if (!nextTune || (!kit.length && !missing.length)) {
      return talkPanelLevel('Your Stage ' + target + ' path isn\'t fully in the catalog for your car yet.',
        'Talk to us — we\'ll build the complete plan.');
    }
    /* C6 — NO-PARTS branch: the next stage tune exists but EW carries ZERO
       fitting hardware for the WHOLE gap (every gap category resolved
       nothing — e.g. N63 Stage 2 = [downpipe], no N63 DP in catalog). We
       still SELL the tune and give a generic source-it-yourself checklist —
       no kit price, no "You save", no bundle, no discount, no TBA framing.
       The PARTIAL case (some parts carried + some TBA) stays priced below. */
    if (!kit.length && missing.length) {
      return renderNoParts(target, nextTune, missing);
    }
    var alc = nextTune.price, items = [{ id: nextTune.variantId }];
    var lines = '<li><strong>Stage ' + target + ' tune</strong> — the calibration that uses the kit.</li>';
    kit.forEach(function (k) {
      alc += k.hw.price;
      items.push({ id: k.hw.variantId });
      lines += '<li><strong>' + k.hw.title + '</strong> — ' + (CAT_LABEL[k.cat] || k.cat) +
        ', a required piece for Stage ' + target + '.</li>';
    });
    /* Branded TBA tile for any recipe slot we don't yet stock for this
       car (the canonical case is V8 downpipes — sourcing deferred).
       Visible so the path reads complete, but NOT priced and NOT added
       to the cart payload (items/alc unchanged above). */
    missing.forEach(function (cat) {
      var lbl = (CAT_LABEL[cat] || cat);
      lbl = lbl.charAt(0).toUpperCase() + lbl.slice(1);
      lines += '<li class="ew-pf-line-tba">' +
        '<span class="ew-tba ew-tba--inline" aria-hidden="true">' +
          '<span class="ew-tba__inner">' +
            '<img class="ew-tba__mark" src="' + EW_LOGO_URL + '" alt="" aria-hidden="true">' +
            '<span class="ew-tba__label">TBA</span>' +
          '</span>' +
        '</span>' +
        '<span class="ew-pf-line-tba__text"><strong>' + lbl +
        '</strong> — coming soon for your car; sourcing in progress, ' +
        'price excluded for now.</span>' +
      '</li>';
    });
    /* Bundles fix (homepage-sweep #1): if a Stage Kit bundle exists for
       (engine, target stage), swap the items list for a single bundle
       line. Matching logic above is unchanged — only the cart payload
       changes. Bundle-swap only applies when the user owns ZERO target-
       stage parts (kit.length == full Core size) — otherwise the bundle
       would re-charge for parts already owned, so we fall back to the
       per-part list (which honors owned-parts deductions). */
    /* F-01c — never build an unsellable kit. Resolution above is UNCHANGED
       (cheapest fitting part per slot — no skipping, no omitting, no
       repricing); AFTER resolution, any required component (the next-stage
       tune itself or any resolved kit part) with available === false makes
       the kit unbuyable online. Tune-only stays purchasable as long as the
       TUNE variant itself is sellable. */
    var tuneSellable = isSellable(nextTune);
    var kitSellable = tuneSellable && kit.every(function (k) { return isSellable(k.hw); });
    var engine = engineFamily((state.vehicle && (state.vehicle.engine_code || state.vehicle.engine)) || '');
    var coreList = (STAGE_TABLES[engine] && STAGE_TABLES[engine][target]) || [];
    var coreFullSize = coreList.length + 1; /* core parts + tune */
    var ownsZeroOfTarget = (items.length === coreFullSize);
    var bundleHit = (D.bundles && D.bundles[engine + '_' + target]) || null;
    var usedBundle = false;
    if (bundleHit && bundleHit.variantId && ownsZeroOfTarget) {
      /* F-01c §3 — bundle-swap guard: an unavailable standing-bundle variant
         is never swapped in; the per-part payload (already vetted sellable by
         kitSellable above) ships instead. Nested, not chained, so the
         ownsZeroOfTarget eligibility line stays byte-intact. */
      if (isSellable(bundleHit)) {
        items = [{ id: bundleHit.variantId }];
        usedBundle = true;
      }
    }
    /* C1: the displayed kit price is ALWAYS computed on the fly from the
       resolver output (cheapest fitting part per slot) at the 8% KIT
       discount — Σ × 0.92. The baked D.bundles[...] variant price is a
       frozen, hand-picked variant that exceeded the resolved Σ and drove
       "You save" NEGATIVE (A2: $-712 B48, $-6,687 S58). We keep the
       bundle-variant SWAP for the cart payload above (usedBundle) but it
       no longer feeds the displayed price. With every hw.price >= 0,
       bundle = round(alc*0.92) <= alc, so save = alc - bundle >= 0. */
    var bundle = Math.round(alc * (1 - KIT_DISCOUNT / 100));
    var save = alc - bundle;
    var tag = isOrphan ? 'Finish what you started — Stage ' + target : 'Level Up — Stage ' + target;
    var framing = isOrphan
      ? 'You already own the big-ticket part — here\'s the rest of the kit to make it count.'
      : 'The complete next step up. Which do you want — good, or great?';
    var honest = missing.length
      ? '<p class="ew-pf-choice__honest">The TBA part above is sourcing-in-progress; the price reflects only the parts in stock today. We\'ll fold the TBA into your kit + the bundle price as soon as it lands.</p>'
      : '<p class="ew-pf-choice__honest">This is the complete hardware set Stage ' + target + ' requires for your car. Parts you already own aren\'t in the kit or the price.</p>';
    /* C1 render guard: NEVER paint a zero/negative "You save" line, even
       mid-migration. Suppress the element entirely when save <= 0. */
    var saveLine = save > 0
      ? '<span class="ew-pf-choice__save">You save ' + money(save) + ' vs piecemeal</span>'
      : '';
    /* C7 / G-2 RELEASE — the Level-Up kit (Path 2) is now PURCHASABLE. The
       C4 Cart Transform (gid://shopify/CartTransform/39682083) is registered
       + active, so the marked parent + component lines collapse into ONE line
       priced at round(Σcents × 0.92) — matching the displayed kit price. The
       buy button below carries the full marked payload on data-pf-kit-add;
       the init() handler routes that → addToCart() (same path as Path 1's
       data-pf-add buy). The former hold (consult trigger + "finalizing this"
       note) is removed. Path 1 (current-stage tune) is unchanged. */
    /* C4 producer: build the FULL marked kit payload — parent tune + each
       resolved component, one shared group id, prices in cents — and emit it
       on the CTA as data-pf-kit-add. On click the handler in init() parses it
       and POSTs every line; the Cart Transform then merges + prices them. The
       kit title mirrors the rendered "complete kit" heading. */
    var kitTitle = 'Stage ' + target + ' Bundle (Tune + Hardware)';
    var kitPayload = encodeURIComponent(JSON.stringify(buildKitItems(nextTune, kit, kitTitle, engine)));
    /* C5: "Just the tune" — a real, purchasable secondary path for the
       customer who already owns the hardware. Adds ONLY the next-stage
       tune as a single line via the existing data-pf-add → addToCart()
       mechanism (Path-1's path), NOT the consult dialog. Guarded on
       nextTune (always present in this priced branch, but defensive).
       Renders for both target stage 2 and 3. */
    var tuneOnly = nextTune && nextTune.variantId
      ? '<div class="ew-pf-choice__tuneonly">' +
          (tuneSellable
            ? '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--tuneonly" data-pf-add="' +
                encodeURIComponent(JSON.stringify([{ id: nextTune.variantId, label: 'Stage ' + target + ' tune', price: nextTune.price, tuneOnly: true }])) +
                '">Just the tune <span class="ew-pf-choice__tuneonly-price">' + money(nextTune.price) + '</span></button>' +
              '<p class="ew-pf-choice__tuneonly-note">Already have the hardware? We\'ll calibrate around it.</p>'
            : '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--tuneonly" disabled>Just the tune <span class="ew-pf-choice__tuneonly-price">' + money(nextTune.price) + '</span></button>') +
        '</div>'
      : '';
    /* F-01c — three sellability states for the buy block:
       (1) kitSellable        → price/save + live gold kit CTA (unchanged);
       (2) hardware piece out → the work-order copy IN PLACE OF the price/
           save block, kit CTA disabled (no data-pf-kit-add → no gold buy
           signal on a dead control), tune-only stays live;
       (3) the TUNE is out    → both CTAs disabled, consult is the live path.
           No copy claiming the tune is available — that would be a lie. */
    var buyBlock;
    if (kitSellable) {
      buyBlock = '<span class="ew-pf-choice__price">' + money(bundle) +
          ' <s class="ew-pf-choice__alc">' + money(alc) + '</s></span>' +
        saveLine +
        '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--levelup" data-pf-kit-add="' + kitPayload + '">' +
          'Add the kit to cart</button>' +
        tuneOnly;
    } else if (tuneSellable) {
      buyBlock = '<p class="ew-pf-choice__unavail">This kit is temporarily incomplete to order online — the tune is available now, and we can quote the hardware directly.</p>' +
        '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--levelup" disabled>Add the kit to cart</button>' +
        tuneOnly;
    } else {
      buyBlock = '<p class="ew-pf-choice__unavail">This package is temporarily unavailable to order online.</p>' +
        '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--levelup" disabled>Add the kit to cart</button>' +
        tuneOnly +
        '<button type="button" class="ew-pf-choice__cta" data-pf-open-s4>Talk to us &rarr;</button>';
    }
    return '<article class="ew-pf-choice ew-pf-choice--c2 ew-pf-choice--levelup">' +
      '<div class="ew-pf-choice__tag ew-pf-choice__tag--levelup">' + tag + '</div>' +
      '<h3 class="ew-pf-choice__title">Stage ' + target + ' — the complete kit</h3>' +
      '<p class="ew-pf-choice__framing">' + framing + '</p>' +
      '<ul class="ew-pf-choice__lines">' + lines + '</ul>' + honest +
      '<div class="ew-pf-choice__power" data-pf-power aria-hidden="true"></div>' +
      /* F-01b — pre-rendered (empty) aria-live region (see the c1 card). */
      '<p class="ew-pf-choice__error" data-pf-error role="status" aria-live="polite"></p>' +
      '<div class="ew-pf-choice__buy">' + buyBlock + '</div></article>';
  }

  /* C6 — NO-PARTS panel. EW carries no fitting hardware for any gap
     category of the next stage, so we sell the tune outright and list the
     parts the customer sources themselves. No price math (no kit/save/
     bundle/discount → no negative possible), no per-part links, no prices,
     no TBA mark, no "coming soon"/"sourcing in progress" language. The
     buyable item reuses C5's single-tune data-pf-add → addToCart() path.
     The closer's "talk to us" opens the consult dialog (data-pf-open-s4,
     which falls back to /pages/contact) — informational, never a quote
     gate or dead-end. */
  function renderNoParts(target, nextTune, missing) {
    var checklist = missing.map(function (cat) {
      return '<li>' + (CAT_LABEL[cat] || cat) + '</li>';
    }).join('');
    var buy = nextTune && nextTune.variantId
      /* F-01b — pre-rendered (empty) aria-live region (see the c1 card). */
      ? '<p class="ew-pf-choice__error" data-pf-error role="status" aria-live="polite"></p>' +
        '<div class="ew-pf-choice__buy">' +
          '<span class="ew-pf-choice__price">' + money(nextTune.price) + '</span>' +
          (isSellable(nextTune)
            ? '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--levelup" data-pf-add="' +
                encodeURIComponent(JSON.stringify([{ id: nextTune.variantId, label: 'Stage ' + target + ' tune', price: nextTune.price }])) +
                '">Add the Stage ' + target + ' tune &rarr;</button>'
            /* F-01c — tune variant unavailable: the buy disables; the
               closer's "Talk to us" below is already the live consult path. */
            : '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--levelup" disabled>Add the Stage ' + target + ' tune &rarr;</button>') +
        '</div>'
      : '';
    return '<article class="ew-pf-choice ew-pf-choice--c2 ew-pf-choice--levelup ew-pf-choice--noparts">' +
      '<div class="ew-pf-choice__tag ew-pf-choice__tag--levelup">Level Up — Stage ' + target + '</div>' +
      '<h3 class="ew-pf-choice__title">What this stage needs</h3>' +
      '<p class="ew-pf-choice__framing">We don\'t stock the hardware for this stage yet, but the tune is ready. ' +
        'Source the parts below and we\'ll calibrate for them once they\'re on the car.</p>' +
      '<ul class="ew-pf-choice__lines ew-pf-choice__lines--checklist">' + checklist + '</ul>' +
      buy +
      '<p class="ew-pf-choice__noparts-closer">Not sure what fits? ' +
        '<button type="button" class="ew-pf-choice__talklink" data-pf-open-s4>Talk to us</button> ' +
        'before you buy — no quote required.</p>' +
      '</article>';
  }

  function talkPanel(h, s) {
    return '<article class="ew-pf-choice ew-pf-choice--talk">' +
      '<div class="ew-pf-choice__tag">Let\'s map it</div>' +
      '<h3 class="ew-pf-choice__title">' + h + '</h3>' +
      '<p class="ew-pf-choice__framing">' + s + '</p>' +
      '<div class="ew-pf-choice__buy"><a class="ew-pf-choice__cta" href="/pages/contact">Talk to us &rarr;</a></div></article>';
  }
  function talkPanelLevel(h, s) {
    return '<article class="ew-pf-choice ew-pf-choice--c2 ew-pf-choice--levelup">' +
      '<div class="ew-pf-choice__tag ew-pf-choice__tag--levelup">Level Up</div>' +
      '<h3 class="ew-pf-choice__title">' + h + '</h3>' +
      '<p class="ew-pf-choice__framing">' + s + '</p>' +
      '<div class="ew-pf-choice__buy"><button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--levelup" data-pf-open-s4>Talk to us &rarr;</button></div></article>';
  }
  /* Stage 4+ panel — the "Level Up" box becomes a dialog trigger */
  function s4Panel(soleResult) {
    return '<article class="ew-pf-choice ew-pf-choice--c2 ew-pf-choice--levelup ew-pf-choice--s4' +
      (soleResult ? ' ew-pf-choice--s4-sole' : '') + '">' +
      '<div class="ew-pf-choice__tag ew-pf-choice__tag--levelup">Stage 4+ — Race</div>' +
      '<h3 class="ew-pf-choice__title">Stage 4+ doesn\'t fit in a shopping cart.</h3>' +
      '<p class="ew-pf-choice__framing">This is where bolt-ons end and builds begin — turbos, forged internals, fuel systems, and decisions best made out loud.</p>' +
      '<div class="ew-pf-choice__buy">' +
        '<button type="button" class="ew-pf-choice__cta ew-pf-choice__cta--levelup" data-pf-open-s4>Talk to us about a Race build &rarr;</button>' +
      '</div></article>';
  }

  /* ---- C4 bundle-group markers (producer side) ----
     Per docs/cart-transform-bundle-group-contract.md §1. A "Level Up" kit
     add emits the FULL marker set on every line so the (parallel) Cart
     Transform Function can merge the lines into ONE at round(Σcents × 0.92).
     C5 (tune-only) and C6 (no-parts) emit NO _ew_* markers — they stay inert
     single lines (contract §2). The kit CTA itself is still HELD (G-2): this
     path is built and correct but lands behind the consult trigger, inert,
     until Phase B un-holds it (contract §7 item 5 / C7). */
  var KIT_VER = '1', KIT_TYPE = 'level-up';
  /* collision-resistant per-add-event group id (one group id = one kit) */
  function newKitGroup() {
    var rand;
    try {
      var a = new Uint32Array(1);
      (window.crypto || window.msCrypto).getRandomValues(a);
      rand = a[0].toString(36);
    } catch (_) {
      rand = Math.floor(Math.random() * 0x100000000).toString(36);
    }
    return 'kit-' + Date.now() + '-' + rand;
  }
  /* Build the marked cart payload for a Level-Up kit:
     one parent (the next-stage tune) + one component per resolved hw.
     Prices are the SAME cents the integrity gate / displayed Σ use
     (nextTune.price, k.hw.price — already integer cents from Liquid). */
  function buildKitItems(nextTune, kit, kitTitle, engineFam) {
    var group = newKitGroup();
    var base = {
      '_ew_kit': 'true',
      '_ew_kit_group': group,
      '_ew_kit_type': KIT_TYPE,
      '_ew_kit_ver': KIT_VER
    };
    function withBase(extra) {
      var p = {}; var k;
      for (k in base) { if (base.hasOwnProperty(k)) p[k] = base[k]; }
      for (k in extra) { if (extra.hasOwnProperty(k)) p[k] = extra[k]; }
      return p;
    }
    var items = [{
      id: nextTune.variantId,
      properties: withBase({
        '_ew_role': 'parent',
        '_ew_kit_title': kitTitle,
        '_ew_comp_price': String(nextTune.price),        /* cents; "0" if free */
        '_ew_engine_fam': engineFam || ''
      })
    }];
    kit.forEach(function (k) {
      var props = withBase({
        '_ew_role': 'component',
        '_ew_comp_price': String(k.hw.price)            /* cents */
      });
      if (k.hw.sku) props['_ew_comp_sku'] = String(k.hw.sku);
      items.push({ id: k.hw.variantId, properties: props });
    });
    return items;
  }

  /* ---- add to cart ----
     Per-line markers (it.properties) are merged onto the shared identity
     props so a kit add carries its _ew_* set while still tagging Vehicle/
     _via. Single-line tune adds (C5/C6, Path 1) pass no it.properties → no
     _ew_* keys → inert. quantity stays 1 per line (one group id = one kit,
     contract §4). The $0-inventory made-to-order tune adds fine: Cart
     Transform does not gate on inventory and _ew_comp_price "0" is a valid
     summand, not an unavailable signal (contract §4). */
  /* ---- GA4 add_to_cart (custom builder path; product-form path is handled
     separately by analytics-tracking via pub/sub). Derive item_category from
     the _ew_kit markers: a marked multi-line add is a 'kit'; a lone tune is
     'tune' (or 'tune_only' when it carries the just-the-tune flag). Prices come
     from _ew_comp_price (cents) when present, else it.price (cents); value is
     the Σ in dollars. Fully guarded — never throws. */
  function trackAddToCart(items) {
    if (!window.ewTrack) return;
    try {
      var isKit = items.some(function (it) {
        return it.properties && it.properties['_ew_kit'] === 'true';
      });
      var totalCents = 0;
      var gaItems = items.map(function (it) {
        var props = it.properties || {};
        var cents = props['_ew_comp_price'] != null
          ? parseInt(props['_ew_comp_price'], 10)
          : (typeof it.price === 'number' ? it.price : 0);
        if (!isNaN(cents)) totalCents += cents;
        var cat = isKit ? 'kit' : (it.tuneOnly ? 'tune_only' : 'tune');
        return {
          item_id: props['_ew_comp_sku'] || it.id,
          item_name: props['_ew_kit_title'] || it.label || ('variant ' + it.id),
          item_category: cat,
          price: (isNaN(cents) ? 0 : cents) / 100,
          quantity: 1
        };
      });
      window.ewTrack('add_to_cart', {
        currency: 'USD',
        value: totalCents / 100,
        items: gaItems
      });
    } catch (e) {}
  }

  /* tune_builder_path_select — the intent signal fired at the result-CTA
     click, BEFORE addToCart(). Distinct from add_to_cart (fired on add
     success): this captures the chosen PATH regardless of cart outcome.
     next_tune is the stage the chosen path targets, parsed from the payload
     line label / kit title ("Stage N …"); detected_stage is the resolved
     stage held in _lastStage. Fully guarded — never throws. */
  function stageFromItems(items) {
    var n = null;
    (items || []).forEach(function (it) {
      if (n != null || !it) return;
      var src = (it.properties && it.properties['_ew_kit_title']) || it.label || '';
      var m = String(src).match(/Stage\s+(\d+)/i);
      if (m) n = parseInt(m[1], 10);
    });
    return n;
  }
  function trackPathSelect(path, items) {
    if (!window.ewTrack) return;
    try {
      window.ewTrack('tune_builder_path_select', {
        path: path,
        detected_stage: _lastStage,
        next_tune: stageFromItems(items)
      });
    } catch (e) {}
  }

  /* ---- F-01b — shared add-to-cart failure recovery ----
     Every add path in this module (Path-1 data-pf-add, "Just the tune",
     C6 no-parts, kit data-pf-kit-add) flows through addToCart(), so
     recovery lives HERE, once: re-enable the CTA, restore its original
     label (captured on the element at click time), and render ONE inline
     aria-live error in the button's choice card — replaced (never
     appended) on repeat failures, cleared on the next successful add.
     The "talk to us" inside the error is the existing consult path
     (data-pf-open-s4; openS4() falls back to /pages/contact). No
     window.alert, no new tracking, success path unchanged. */
  var ADD_FAIL_FALLBACK = 'the cart couldn\'t accept one of the parts';
  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function restoreAddButton(btn) {
    if (!btn) return;
    btn.disabled = false;
    if (btn._ewPfLabel != null) btn.innerHTML = btn._ewPfLabel;
  }
  function addErrorEl(btn) {
    var card = btn && btn.closest ? btn.closest('.ew-pf-choice') : null;
    return card ? card.querySelector('[data-pf-error]') : null;
  }
  function clearAddError(btn) {
    /* Empty, never remove — the pre-rendered live region must persist so a
       later failure in the same card still announces. */
    var err = addErrorEl(btn);
    if (err) err.textContent = '';
  }
  /* reason === null → network failure (fetch rejected, no response). */
  function showAddFailure(btn, reason) {
    restoreAddButton(btn);
    var card = btn && btn.closest ? btn.closest('.ew-pf-choice') : null;
    if (!card) return;
    /* Primary path: the EMPTY live region is pre-rendered in every buyable
       card template, so AT already tracks it and the fill announces. The
       create-and-insert branch below is a safety fallback only. */
    var err = card.querySelector('[data-pf-error]');
    if (!err) {
      err = document.createElement('p');
      err.className = 'ew-pf-choice__error';
      err.setAttribute('data-pf-error', '');
      err.setAttribute('role', 'status');
      err.setAttribute('aria-live', 'polite');
      var buy = card.querySelector('.ew-pf-choice__buy');
      if (buy) card.insertBefore(err, buy); else card.appendChild(err);
    }
    var talk = '<button type="button" class="ew-pf-choice__error-talk" data-pf-open-s4>talk to us</button>';
    if (reason === null) {
      err.innerHTML = 'That didn\'t go through — a network problem stopped it. Try again, or ' + talk + '.';
    } else {
      /* Cards holding a LIVE "Just the tune" sibling (the kit path) get the
         tune-alone pointer; cards without one (Path 1 / the tune-only buy
         itself / no-parts) end on the consult path instead. */
      var liveTuneOnly = card.querySelector('.ew-pf-choice__cta--tuneonly:not([disabled])');
      var pointTuneAlone = !!liveTuneOnly &&
        !(btn && btn.classList && btn.classList.contains('ew-pf-choice__cta--tuneonly'));
      err.innerHTML = pointTuneAlone
        ? 'That didn\'t go through — ' + escapeHtml(reason) + '. The tune alone is available now, or ' + talk + '.'
        : 'That didn\'t go through — ' + escapeHtml(reason) + '. Try again, or ' + talk + '.';
    }
  }
  function addToCart(items, btn) {
    /* F-05 — the 'Vehicle' line property (key LOCKED — FUL-1's packing slip
       reads it) gets the canonical cart formatter: year-known →
       "2021 BMW 330i · G20 · B48"; unknown → the honest band
       "2019–2025 G20 330i · B48". vehicleLabel() is the resolver-absent
       fallback (same provenance rule, display form). */
    var R = window.EWVehicleResolver;
    var vehicleProp = (R && typeof R.cartVehicleProperty === 'function' && state.vehicle)
      ? R.cartVehicleProperty(state.vehicle)
      : vehicleLabel(state.vehicle);
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items.map(function (it) {
        var props = { 'Vehicle': vehicleProp, '_via': 'tune-path-finder' };
        if (it.properties) {
          for (var k in it.properties) {
            if (it.properties.hasOwnProperty(k)) props[k] = it.properties[k];
          }
        }
        return { id: it.id, quantity: 1, properties: props };
      }) })
    }).then(function (r) {
      if (!r.ok) {
        /* Shopify /cart/add.js error body: { status, message, description }.
           Surface description/message as the inline reason — first letter
           lowercased, trailing punctuation dropped (the copy supplies its
           own). Unparsable body → the generic fallback. */
        return r.json().then(function (j) {
          var msg = String((j && (j.description || j.message)) || '').trim().replace(/[.!\s]+$/, '');
          return msg ? msg.charAt(0).toLowerCase() + msg.slice(1) : ADD_FAIL_FALLBACK;
        }, function () { return ADD_FAIL_FALLBACK; })
          .then(function (reason) { showAddFailure(btn, reason); });
      }
      clearAddError(btn);
      trackAddToCart(items);
      window.location.href = '/cart';
    }, function () {
      showAddFailure(btn, null); /* fetch rejected — network, no response */
    });
  }

  /* ---- Stage 4+ dialog ---- */
  var dlg, lastFocus;
  function wireS4() {
    dlg = document.querySelector('[data-pf-s4-dialog]');
  }
  function openS4() {
    if (!dlg) dlg = document.querySelector('[data-pf-s4-dialog]');
    if (!dlg) { window.location.href = '/pages/contact'; return; }
    lastFocus = document.activeElement;
    /* prefill vehicle + a distinctive subject for LP's inbox.
       F-05 — year only with provenance (year_known); otherwise the chassis
       + engine alone. Never the band-end styled as a model year. */
    var v = state.vehicle || {};
    var ctx = [(v.year_known === true ? v.year : null), chassisOf(v), engineOf(v)].filter(Boolean).join(' ');
    var vf = dlg.querySelector('[data-pf-s4-vehicle]'); if (vf) vf.value = ctx || 'Vehicle TBD';
    var sf = dlg.querySelector('[data-pf-s4-subject]'); if (sf) sf.value = '🏁 STAGE 4+ BUILD — ' + (ctx || 'vehicle TBD');
    dlg.hidden = false;
    document.body.style.overflow = 'hidden';
    var f = dlg.querySelector('input, textarea, button, a'); if (f) f.focus();
    document.addEventListener('keydown', onKey, true);
  }
  function closeS4() {
    if (!dlg) return;
    dlg.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey, true);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  function onKey(e) {
    if (e.key === 'Escape') { closeS4(); return; }
    if (e.key !== 'Tab' || !dlg) return;
    var f = dlg.querySelectorAll('a[href], button:not([disabled]), input, textarea');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---- wire ---- */
  function init() {
    headRefs(); /* capture the server-rendered input-state header before any state swap */
    /* Entry-vehicle precedence (BUGS-1 Bug 3 — ONE identity layer). The
       Tune Builder takes its vehicle from the shared layer (persisted state +
       shared resolver), never its own dataset. Three entry paths:

         1. No ?chassis= param — the common no-param path. Stored Garage
            vehicle wins, painted synchronously (localStorage, no dataset).
         2. Param present AND it matches the stored vehicle's chassis code —
            the dialog -> funnel handoff (source="tune" wrote the FULL
            resolution incl. trim, then routed here with just the code). CARRY
            the stored vehicle as-is, synchronously — re-deriving from the
            dataset would drop the trim. Strip the param so a later in-dialog
            change isn't overridden on re-entry/refresh.
         3. Param present but it does NOT match stored (external deep-link /
            code-only CTA) — resolve via the shared resolver. Pre-paint from
            stored (sync, no blank flash), then DEFER the param resolution
            until the resolver's dataset is ready and apply it. */
    var stored = readVehicle();
    var paramChassis = '';
    try {
      var qs = new URLSearchParams(window.location.search);
      paramChassis = String(qs.get('chassis') || qs.get('vehicle') || '').toUpperCase().trim();
    } catch (_) {}

    if (!paramChassis) {
      /* No param — the common no-param path. Stored vehicle wins, sync. */
      state.vehicle = stored;
      paintVehicle();
      repaintWhenResolverReady(); /* F-05: re-render label once resolver appears */
      showStep(stored && chassisOf(stored) ? 'mods' : 'vehicle');
    } else if (stored && chassisOf(stored) === paramChassis) {
      /* Dialog -> funnel handoff: the param is just the chassis code, but the
         stored vehicle is the dialog's full resolution (incl. TRIM). Carry the
         stored vehicle as-is — re-deriving from the dataset would drop the
         trim. SYNCHRONOUS, no dataset needed. */
      state.vehicle = stored;
      stripVehicleParams();
      paintVehicle();
      repaintWhenResolverReady(); /* F-05: re-render label once resolver appears */
      showStep(chassisOf(stored) ? 'mods' : 'vehicle');
    } else {
      /* External deep-link / code-only CTA: the param does NOT match the
         stored vehicle. Resolve via the shared resolver. Pre-paint from the
         stored vehicle first (sync) so there's no blank flash, then apply the
         param-resolved vehicle once the resolver's dataset is ready. */
      state.vehicle = stored;
      paintVehicle();
      showStep(stored && chassisOf(stored) ? 'mods' : 'vehicle');

      var applyParamVehicle = function () {
        var pv = vehicleFromParams();
        if (!pv) return;
        persistVehicle(pv);
        state.vehicle = pv;
        stripVehicleParams();
        paintVehicle();
        showStep(chassisOf(pv) ? 'mods' : 'vehicle');
      };
      /* The shared resolver (ew-vehicle-resolver.js) loads via vehicle-data-island,
         which in document order executes AFTER this section's deferred script — so
         window.EWVehicleResolver may not exist YET when init() runs. Wait for it
         (bounded poll), then resolve the deep-link against its loaded dataset. If it
         never appears (~5s), fall back to a synchronous resolve — vehicleFromParams()
         has its own tuneFor() engine fallback so the deep link still resolves. */
      var resolverTries = 0;
      (function awaitResolver() {
        var R = window.EWVehicleResolver;
        if (R && typeof R.ready === 'function') { R.ready().then(applyParamVehicle); return; }
        if (++resolverTries > 50) { applyParamVehicle(); return; }
        setTimeout(awaitResolver, 100);
      })();
    }
    wireS4();

    /* A vehicle change from any surface that shares our identity layer
       (the resolver fires ew:vehicle-resolved; the Garage dialog fires
       both ew:vehicle-resolved via the resolver write AND
       ew:vehicle-changed) re-syncs the Tune Builder so an in-dialog
       Save actually takes — same handler on both event names. */
    function onVehicleChange(e) {
      state.vehicle = (e && e.detail) || readVehicle(); paintVehicle(); showStep('mods');
    }
    document.addEventListener('ew:vehicle-resolved', onVehicleChange);
    document.addEventListener('ew:vehicle-changed', onVehicleChange);
    document.addEventListener('ew:vehicle-cleared', function () {
      state.vehicle = null; paintVehicle(); showStep('vehicle');
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-pf-s4-close]')) { closeS4(); return; }
      if (dlg && !dlg.hidden && e.target === dlg) { closeS4(); return; }
    });
    root.addEventListener('click', function (e) {
      var act = e.target.closest('[data-pf-action]');
      if (act) {
        var a = act.getAttribute('data-pf-action');
        if (a === 'to-vehicle') showStep('vehicle');
        else if (a === 'compute') compute();
        else if (a === 'restart') showStep(state.vehicle ? 'mods' : 'vehicle');
        return;
      }
      if (e.target.closest('[data-pf-open-s4]')) { openS4(); return; }
      /* C4 kit add (ACTIVE — C7/G-2 release): the Level-Up CTA carries the
         marked parent + component payload on data-pf-kit-add. On click we
         parse it and POST every line so the Cart Transform merges them into
         one line at round(Σ × 0.92). (Legacy fallback to data-pf-kit is kept
         for safety; no live CTA uses it.) */
      var kitAdd = e.target.closest('[data-pf-kit-add]');
      if (kitAdd && kitAdd.tagName === 'BUTTON') {
        kitAdd._ewPfLabel = kitAdd.innerHTML; /* F-01b — original label, restored on failure */
        kitAdd.disabled = true; kitAdd.textContent = 'Adding…';
        var kitItems;
        try { kitItems = JSON.parse(decodeURIComponent(kitAdd.getAttribute('data-pf-kit-add') || kitAdd.getAttribute('data-pf-kit'))); } catch (_) { kitItems = []; }
        /* tune_builder_path_select — intent signal, BEFORE addToCart. The kit
           CTA always targets the next stage (Level-Up). Guarded; never throws. */
        trackPathSelect('kit', kitItems);
        if (kitItems.length) addToCart(kitItems, kitAdd);
        else showAddFailure(kitAdd, ADD_FAIL_FALLBACK); /* unparsable payload — recover, never strand the button */
        return;
      }
      var add = e.target.closest('[data-pf-add]');
      if (add && add.tagName === 'BUTTON') {
        add._ewPfLabel = add.innerHTML; /* F-01b — original label, restored on failure */
        add.disabled = true; add.textContent = 'Adding…';
        var items;
        try { items = JSON.parse(decodeURIComponent(add.getAttribute('data-pf-add'))); } catch (_) { items = []; }
        /* tune_builder_path_select — BEFORE addToCart. A data-pf-add carrying
           the tuneOnly flag is the "Just the tune" Level-Up path; a plain
           data-pf-add is the current-stage / level-up tune purchase. */
        var isTuneOnly = items.some(function (it) { return it && it.tuneOnly; });
        trackPathSelect(isTuneOnly ? 'tune_only' : 'tune', items);
        if (items.length) addToCart(items, add);
        else showAddFailure(add, ADD_FAIL_FALLBACK); /* unparsable payload — recover, never strand the button */
      }
    });

    /* Stage 4+ contact form — AJAX submit so it stays in-flow */
    var form = document.querySelector('.ew-pf-s4__form');
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'Accept': 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error('send');
          var c = dlg.querySelector('[data-pf-s4-confirm]');
          if (c) { c.hidden = false; }
          form.hidden = true;
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = 'Send'; }
          window.alert('Sorry — that didn\'t send. Please call us, or try again.');
        });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.EWTuneBuilder = { compute: compute };
  window.EWPathFinder = window.EWTuneBuilder; /* back-compat alias (deprecated) */
})();
